import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, UserCog, Users, UsersRound } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { formatSydneyDateShort } from '../../../utils/helpers';
import { HrBarList } from '../components/BarList';
import { HrDataTable } from '../components/DataTable';
import type { HrColumn } from '../components/DataTable';
import { HrKpiGrid } from '../components/KpiGrid';
import { HrStatusPill } from '../components/StatusPill';
import {
  WIDE_SPAN,
  countBy,
  percent,
  sortCountsDesc,
} from '../hr-dashboard-utils';
import type { HrPerson } from '../../../types/hr-dashboard.types';
import type { HrSectionProps } from './types';

type BreakdownView = 'Department' | 'Job role' | 'Role level' | 'Region';

const breakdownKey = (person: HrPerson, view: BreakdownView): string => {
  switch (view) {
    case 'Department':
      return person.dept || 'Not recorded';
    case 'Job role':
      return person.role || 'Not recorded';
    case 'Role level':
      return person.level;
    default:
      return person.region;
  }
};

const EMPLOYMENT_COLOURS: Record<string, string> = {
  'Permanent FT': 'var(--chart-3)',
  'Permanent PT': 'var(--chart-2)',
  Casual: 'var(--chart-5)',
  'Maximum Term FT': 'var(--chart-4)',
  'Maximum Term PT': 'var(--chart-1)',
  'Not recorded': 'var(--muted-foreground)',
};

export function HeadcountSection({
  people,
  filters,
  asOf,
  canExport,
  checkExport,
  toggleDept,
}: HrSectionProps) {
  const [view, setView] = useState<BreakdownView>('Department');

  const permanent = people.filter((person) => /^Permanent/.test(person.emp || '')).length;
  const casual = people.filter((person) => person.emp === 'Casual').length;
  const unrecorded = people.filter((person) => !person.emp).length;
  const departments = new Set(people.map((person) => person.dept).filter(Boolean)).size;

  const managerMap = new Map<string, { managerId: number; manager: string; reports: number }>();
  people.forEach((person) => {
    if (person.managerId === null || person.managerId === undefined) return;
    const key = String(person.managerId);
    const entry = managerMap.get(key) || {
      managerId: person.managerId,
      manager: person.mgr || 'Unknown',
      reports: 0,
    };
    entry.reports += 1;
    managerMap.set(key, entry);
  });
  const managers = [...managerMap.values()].sort((left, right) => right.reports - left.reports);
  const averageSpan =
    managers.length > 0
      ? people.filter((person) => person.managerId != null).length / managers.length
      : null;

  const breakdownItems = sortCountsDesc(
    countBy(people, (person) => breakdownKey(person, view))
  ).map(([label, value]) => ({
    key: label,
    label,
    value,
    hint: `${value} people · ${percent(value, people.length)}%`,
  }));

  const employmentCounts = countBy(people, (person) => person.emp || 'Not recorded');
  const employmentParts = [...employmentCounts.entries()].map(([label, value]) => ({
    label,
    value,
    color: EMPLOYMENT_COLOURS[label] || 'var(--chart-3)',
  }));

  const roleLevelItems = sortCountsDesc(countBy(people, (person) => person.level)).map(
    ([label, value]) => ({ key: label, label, value })
  );

  const spanRows = managers.map((entry) => {
    const record = people.find((person) => person.id === entry.managerId);
    return {
      manager: entry.manager,
      dept: record?.dept || 'Not an active employee',
      reports: entry.reports,
    };
  });

  const columns: HrColumn<HrPerson>[] = [
    {
      key: 'name',
      header: 'Name',
      value: (person) => person.name,
      render: (person) => (
        <Link to={`/people/${person.id}`} className="text-link hover:underline">
          {person.name}
        </Link>
      ),
    },
    { key: 'dept', header: 'Department', value: (person) => person.dept || '—' },
    { key: 'role', header: 'Job role', value: (person) => person.role || '—' },
    {
      key: 'emp',
      header: 'Type',
      value: (person) => person.emp || 'Not recorded',
      render: (person) =>
        person.emp ? (
          <span>{person.emp}</span>
        ) : (
          <HrStatusPill tone="warning">Not recorded</HrStatusPill>
        ),
    },
    {
      key: 'start',
      header: 'Start',
      value: (person) => person.start || '',
      render: (person) => formatSydneyDateShort(person.start) || '—',
    },
    {
      key: 'status',
      header: 'Status',
      value: (person) => person.status,
      render: (person) =>
        person.status === 'Onboarding' ? (
          <HrStatusPill tone="link">Onboarding</HrStatusPill>
        ) : (
          <HrStatusPill tone="success">Active</HrStatusPill>
        ),
    },
  ];

  const spanColumns: HrColumn<{ manager: string; dept: string; reports: number }>[] = [
    { key: 'manager', header: 'Manager', value: (row) => row.manager },
    {
      key: 'dept',
      header: 'Department',
      value: (row) => row.dept,
      render: (row) =>
        row.dept === 'Not an active employee' ? (
          <HrStatusPill tone="warning">Not active</HrStatusPill>
        ) : (
          row.dept
        ),
    },
    {
      key: 'reports',
      header: 'Reports',
      align: 'right',
      value: (row) => row.reports,
      render: (row) => (
        <span className="inline-flex items-center gap-2">
          <strong className="tabular-nums">{row.reports}</strong>
          {row.reports > WIDE_SPAN ? <HrStatusPill tone="warning">Wide</HrStatusPill> : null}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <HrKpiGrid
        items={[
          { label: 'Active employees', value: people.length, icon: <Users className="h-5 w-5" /> },
          {
            label: 'Departments',
            value: departments,
            icon: <Building2 className="h-5 w-5" />,
          },
          {
            label: 'Permanent',
            value: permanent,
            detail: `${percent(permanent, people.length)}% of headcount`,
            icon: <UsersRound className="h-5 w-5" />,
          },
          {
            label: 'Casual',
            value: casual,
            detail: `${percent(casual, people.length)}% of headcount`,
            icon: <UserCog className="h-5 w-5" />,
          },
          {
            label: 'Type not recorded',
            value: unrecorded,
            detail: 'Chase through the gap register',
            tone: unrecorded > 0 ? 'warning' : 'primary',
            icon: <UserCog className="h-5 w-5" />,
          },
          {
            label: 'Avg span of control',
            value: averageSpan === null ? '—' : averageSpan.toFixed(1),
            detail: `${managers.length} people managers`,
            icon: <Users className="h-5 w-5" />,
          },
        ]}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <Card className="lg:col-span-7">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Headcount by {view.toLowerCase()}</CardTitle>
            <CardDescription>
              Switch the view · department bars filter every page
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Tabs value={view} onValueChange={(value) => setView(value as BreakdownView)}>
              <TabsList className="w-full justify-start overflow-x-auto">
                {(['Department', 'Job role', 'Role level', 'Region'] as BreakdownView[]).map(
                  (option) => (
                    <TabsTrigger key={option} value={option} className="flex-none px-3">
                      {option}
                    </TabsTrigger>
                  )
                )}
              </TabsList>
            </Tabs>
            <HrBarList
              items={breakdownItems.slice(0, 20)}
              onSelect={view === 'Department' ? toggleDept : undefined}
              selectedKeys={view === 'Department' ? filters.dept : []}
            />
            {breakdownItems.length > 20 ? (
              <p className="text-xs text-muted-foreground">
                + {breakdownItems.length - 20} more · see the people list
              </p>
            ) : null}
          </CardContent>
        </Card>

        <Card className="lg:col-span-5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Employment type</CardTitle>
            <CardDescription>Share of active headcount</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex h-5 overflow-hidden rounded-md bg-muted">
                {employmentParts.map((part) =>
                  part.value > 0 ? (
                    <div
                      key={part.label}
                      title={`${part.label}: ${part.value}`}
                      style={{
                        width: `${percent(part.value, people.length)}%`,
                        backgroundColor: part.color,
                      }}
                    />
                  ) : null
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                {employmentParts.map((part) => (
                  <span key={part.label} className="inline-flex items-center gap-1.5">
                    <span
                      className="h-2.5 w-2.5 rounded-sm"
                      style={{ backgroundColor: part.color }}
                    />
                    <span className="text-muted-foreground">{part.label}</span>
                    <strong className="tabular-nums text-foreground">{part.value}</strong>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h3 className="mb-2 text-sm font-semibold text-foreground">Role level mix</h3>
              <HrBarList items={roleLevelItems.slice(0, 10)} />
              <p className="mt-2 text-xs text-muted-foreground">
                Role level is derived from the job role title until the maintainable lookup table
                is added.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <Card className="lg:col-span-5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Span of control</CardTitle>
            <CardDescription>Direct reports per manager</CardDescription>
          </CardHeader>
          <CardContent className="p-3">
            <HrDataTable
              rows={spanRows}
              columns={spanColumns}
              initialSort={{ key: 'reports', direction: 'desc' }}
              searchPlaceholder="Search managers…"
              emptyLabel="No people managers found"
              viewName="Span of control"
              filters={filters}
              asOf={asOf}
              canExport={canExport}
              onBeforeExport={checkExport}
              rowKey={(row) => row.manager}
              maxHeight="22rem"
            />
          </CardContent>
        </Card>

        <Card className="lg:col-span-7">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">People list</CardTitle>
            <CardDescription>Sortable, searchable and respects all filters</CardDescription>
          </CardHeader>
          <CardContent className="p-3">
            <HrDataTable
              rows={people}
              columns={columns}
              initialSort={{ key: 'name', direction: 'asc' }}
              searchPlaceholder="Search people, departments or roles…"
              emptyLabel="No people found"
              viewName="People list"
              filters={filters}
              asOf={asOf}
              canExport={canExport}
              onBeforeExport={checkExport}
              rowKey={(person) => person.id}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
